import { StyleSheet, View } from 'react-native';
import { Badge } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { tokens } from '../../../theme/tokens';

const AUTO_TAG_PREFIX = 'seg:';

interface LeadTagChipsProps {
  tags: string[];
  maxVisible?: number;
}

function isAutoTag(tag: string): boolean {
  return tag.startsWith(AUTO_TAG_PREFIX);
}

function tagLabel(tag: string): string {
  return isAutoTag(tag) ? tag.slice(AUTO_TAG_PREFIX.length) : tag;
}

export function LeadTagChips({ tags, maxVisible = 3 }: Readonly<LeadTagChipsProps>) {
  const { t } = useTranslation();

  if (!tags || tags.length === 0) {
    return null;
  }

  const visible = tags.slice(0, maxVisible);
  const hiddenCount = tags.length - visible.length;

  return (
    <View
      style={styles.container}
      accessibilityRole="list"
      accessibilityLabel={t('leads.list.tagsListLabel')}
    >
      {visible.map((tag) => {
        const auto = isAutoTag(tag);
        const label = tagLabel(tag);
        const accessibilityLabel = t(auto ? 'leads.list.autoTagLabel' : 'leads.list.tagLabel', {
          tag: label,
        });

        return (
          <Badge
            key={tag}
            variant="outline"
            color={auto ? 'purple' : 'neutral'}
            size="sm"
            accessibilityLabel={accessibilityLabel}
          >
            {label}
          </Badge>
        );
      })}

      {hiddenCount > 0 && (
        <Badge
          variant="subtle"
          color="neutral"
          size="sm"
          accessibilityLabel={t('leads.list.tagsMore', { count: hiddenCount })}
        >
          {t('leads.list.tagsMore', { count: hiddenCount })}
        </Badge>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
});
